import {
  settingsGet,
  settingsPut,
  updateUserSettingsSchema,
  userSettingsSchema,
  type UpdateUserSettings,
  type UserSettings,
} from '@hf/sharedContract';
import { apiUrl } from '../http/apiUrl';

export type SettingsClientResult = { ok: true; data: UserSettings } | { ok: false; status: number };

export const SETTINGS_REQUEST_TIMEOUT_MS = 8_000;

type TokenResolver = () => Promise<string | undefined>;

async function authorizedJson(
  path: string,
  method: string,
  resolveToken: TokenResolver,
  init: { json?: unknown } = {},
): Promise<{ status: number; body: unknown }> {
  const token = await resolveToken();
  if (!token) return { status: 401, body: null };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SETTINGS_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(apiUrl(path), {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
      signal: controller.signal,
    });
    const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);
    return { status: response.status, body };
  } catch {
    return { status: 0, body: null };
  } finally {
    clearTimeout(timer);
  }
}

/** GET /settings — returns null-shaped failure when the network or payload is unusable. */
export async function fetchUserSettings(resolveToken: TokenResolver): Promise<SettingsClientResult> {
  const { status, body } = await authorizedJson(settingsGet.path, 'GET', resolveToken);
  if (status !== 200) return { ok: false, status };
  const parsed = userSettingsSchema.safeParse(body);
  return parsed.success ? { ok: true, data: parsed.data } : { ok: false, status: 502 };
}

/** PUT /settings — partial merge (`themeMode`, `analyticsConsent`, …). */
export async function updateUserSettings(resolveToken: TokenResolver, patch: UpdateUserSettings): Promise<SettingsClientResult> {
  const validated = updateUserSettingsSchema.safeParse(patch);
  if (!validated.success) return { ok: false, status: 400 };

  const { status, body } = await authorizedJson(settingsPut.path, 'PUT', resolveToken, { json: validated.data });
  if (status !== 200) return { ok: false, status };
  const parsed = userSettingsSchema.safeParse(body);
  return parsed.success ? { ok: true, data: parsed.data } : { ok: false, status: 502 };
}
